import { useCallback, useMemo, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { cancelOverride, createOverride } from '../api/client'
import type { ApiError, OverrideWindow } from '../api/types'
import { useSchedule } from '../hooks/useSchedule'
import { explainDraft, fmtRange, overlayDraft } from '../lib/schedule'
import { ActionChip } from '../components/ActionChip'
import { AddWindowForm, Draft } from '../components/AddWindowForm'
import { ScheduleTimeline } from '../components/ScheduleTimeline'
import { WindowList } from '../components/WindowList'
import { RelativeTime } from '../components/RelativeTime'

interface Props {
  systemId: number
}

/** The Battery schedule page: plan, windows, and the add form. */
export function Schedule({ systemId }: Props) {
  const { instance } = useMsal()
  const { result, refresh } = useSchedule(systemId)
  const [draft, setDraft] = useState<Draft>({ start: null, end: null, behaviour: null })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const onDraftChange = useCallback((d: Draft) => setDraft(d), [])

  const data = result.state === 'ready' ? result.data : null
  const preview = useMemo(
    () => overlayDraft(data?.schedule ?? [], draft.start, draft.end, draft.behaviour),
    [data, draft],
  )
  const explanation = useMemo(
    () => explainDraft(preview, draft.behaviour, draft.start, draft.end),
    [preview, draft],
  )

  if (result.state === 'loading') {
    return <p className="muted">Loading your battery schedule…</p>
  }
  if (result.state === 'error') {
    return (
      <div className="card">
        <p>{result.error.code === 'not_enabled'
          ? 'Battery schedule changes are not switched on for your system.'
          : "We couldn't load your battery schedule. Please try again shortly."}</p>
        <button onClick={refresh}>Try again</button>
      </div>
    )
  }

  const d = result.data
  const standby = d.control_mode === 'STANDBY'
  const off = d.limits.mode === 'off'
  const activeWindow = d.windows.find((w) => w.display_status === 'ACTIVE' || w.display_status === 'CANCELLING')

  const submit = async (x: { start: Date; end: Date; behaviour: OverrideWindow['behaviour']; price: number | null }) => {
    await createOverride(instance, {
      system_id: d.system_id,
      window_start: x.start.toISOString(),
      window_end: x.end.toISOString(),
      behaviour: x.behaviour,
      price_p_per_kwh: x.price,
    })
    setDraft({ start: null, end: null, behaviour: null })
    await refresh()
  }

  const cancel = async (w: OverrideWindow) => {
    if (!window.confirm(`Cancel the window ${fmtRange(w.window_start, w.window_end)}?`)) return
    setBusyId(w.request_id)
    setNotice(null)
    try {
      const r = await cancelOverride(instance, w.request_id)
      setNotice(r.message)
      await refresh()
    } catch (err) {
      setNotice((err as ApiError).message || 'Could not cancel. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="schedule-page">
      <div className="schedule-intro">
        <p>
          Tell us about cheap or free electricity your supplier offers that we can't see automatically,
          or choose exactly what your battery does for a while.
        </p>
        <ActionChip action={d.action} activeWindow={activeWindow} />
      </div>

      {standby && (
        <div className="banner banner-warning" role="alert">
          <strong>Your system is in standby.</strong> It won't act on schedule changes, so new windows
          can't be added, and any that were set have been cancelled. Contact EquiVolt if you weren't expecting this.
        </div>
      )}
      {!standby && off && (
        <div className="banner banner-warning" role="alert">
          <strong>Schedule changes are paused.</strong> Your battery is following its normal plan.
        </div>
      )}
      {!standby && d.limits.mode === 'dry_run' && (
        <div className="banner banner-info">
          <strong>Trial mode.</strong> Windows are recorded, but your battery won't act on them yet.
        </div>
      )}
      {notice && <div className="banner banner-info" role="status">{notice}</div>}

      <section className="card">
        <div className="card-heading">
          <h2>Next 48 hours</h2>
          {d.schedule_generated_at && (
            <span className="muted">Updated <RelativeTime timestamp={d.schedule_generated_at} /></span>
          )}
        </div>
        <ScheduleTimeline slots={preview} tariff={d.tariff} showBefore={!!draft.behaviour && preview.some((p) => p.inDraft)} />
        <p className="muted small">
          This is the plan. A full battery holds rather than charges, and the battery never goes below its reserve level.
        </p>
      </section>

      <WindowList
        windows={d.windows}
        maxOpen={d.limits.max_open}
        action={d.action}
        busyId={busyId}
        onCancel={cancel}
        cancelDisabled={false}
      />

      <AddWindowForm
        level={d.customer_overrides}
        limits={d.limits}
        tariff={d.tariff}
        windows={d.windows}
        disabled={standby || off}
        explanation={explanation}
        onDraftChange={onDraftChange}
        onSubmit={submit}
      />
    </div>
  )
}
